<template>
    <component :is="tag" :class="[{ 'is-open': visible }, color]" class="accordion-item">
        <button
            type="button"
            :id="headerId"
            ref="header"
            class="accordion-button"
            :aria-expanded="visible.toString()"
            :aria-controls="`accPanel-${id}`"
            :aria-disabled="accordionListState.disabled"
            :disabled="accordionListState.disabled"
            :class="{ 'is-open': ownVisibility }"
            :tabindex="!accordionListState.disabled ? 0 : null"
            v-on="listener || {}"
        >
            <slot name="title" :is-open="visible" :is-disabled="accordionListState.disabled" />
        </button>
        <div
            class="accordion-content"
            :id="`accPanel-${id}`"
            role="region"
            :aria-labelledby="`accHeader-${id}`"
            :class="{ 'initially-hidden': !props.visibility && !accordionListState.disabled }"
        >
            <slot name="content" :is-open="visible" :is-disabled="accordionListState.disabled" />
        </div>
    </component>
</template>

<script>
let nextId = 0;
</script>

<script setup>
import { toggle } from 'slide-element';
import { inject, ref, computed, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    color: {
        type: String,
        default: 'gray',
    },
    name: {
        type: String,
        default: '',
        required: true,
    },
    visibility: {
        type: Boolean,
        default: false,
    },
    tag: {
        type: String,
        default: 'li',
    },
});

const accordionListState = inject('accordionListState');

const header = ref(null);
const id = nextId++;

// Reactive visibility
const ownVisibility = ref(props.visibility);

// Computed values
const headerId = computed(() => `accHeader-${id}`);

const visible = computed(() => {
    if (accordionListState.individual && id !== null) {
        return accordionListState.activeItem === id;
    } else {
        return accordionListState.disabled ? accordionListState.disabled : ownVisibility.value;
    }
});

const listener = computed(() => {
    return accordionListState.disabled
        ? null
        : {
              click: toggleVisibility,
          };
});

// Methods
function toggleVisibility(event) {
    if (!event || (event && !event.keyCode) || (event && event.keyCode === 13)) {
        if (accordionListState.individual) {
            accordionListState.activeItem = visible.value ? null : id;
        } else {
            ownVisibility.value = !ownVisibility.value;
        }
        toggle(event.currentTarget.nextElementSibling);
    }
}

function close() {
    if (accordionListState.individual) {
        accordionListState.activeItem = null;
    } else {
        ownVisibility.value = false;
    }
}
defineExpose({ close });

onMounted(() => {
    accordionListState?.registerHeader(header.value);
});

onBeforeUnmount(() => {
    accordionListState?.unregisterHeader(header.value);
});
</script>

<style lang="scss" scoped>
.accordion-button {
    width: 100%;
    text-align: left;
    cursor: pointer;
    border-radius: 20px;
    border: 0;
    padding: 0;
    .is-open & {
        border-radius: 20px 20px 0 0;
    }
    &[disabled] {
        cursor: default;
    }
}
.accordion-content {
    &.initially-hidden {
        display: none;
    }
}
</style>
